import {clsx} from 'clsx';
import {Popover as RadixPopoverNS} from 'radix-ui';
import type {ComponentProps, ReactNode, Ref} from 'react';

import type {WithoutClassName} from '#types';

import {Arrow} from './arrow/arrow.js';
import {
  borderlessPopoverContent,
  popoverAnchor,
  popoverContent,
} from './style.css.js';
const RadixPopover = RadixPopoverNS.Root;
const RadixPopoverAnchor = RadixPopoverNS.Anchor;
const RadixPopoverArrow = RadixPopoverNS.Arrow;
const RadixPopoverClose = RadixPopoverNS.Close;
const RadixPopoverContent = RadixPopoverNS.Content;
const RadixPopoverPortal = RadixPopoverNS.Portal;
const RadixPopoverTrigger = RadixPopoverNS.Trigger;

export enum PopoverPlacement {
  TOP = 'top',
  TOP_START = 'top-start',
  TOP_END = 'top-end',
  RIGHT = 'right',
  RIGHT_START = 'right-start',
  RIGHT_END = 'right-end',
  BOTTOM = 'bottom',
  BOTTOM_START = 'bottom-start',
  BOTTOM_END = 'bottom-end',
  LEFT = 'left',
  LEFT_START = 'left-start',
  LEFT_END = 'left-end',
}

export interface PopoverRootProps extends Omit<
  ComponentProps<typeof RadixPopover>,
  'className'
> {
  children?: ReactNode;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
}

const Root = RadixPopover;
Root.displayName = 'Popover.Root';

const Portal = RadixPopoverPortal;
Portal.displayName = 'Popover.Portal';

type AnchorProps = WithoutClassName<
  ComponentProps<typeof RadixPopoverAnchor>
> & {
  ref?: Ref<HTMLDivElement>;
};

const Anchor = (props: AnchorProps) => {
  const {ref, ...rest} = props;
  return <RadixPopoverAnchor ref={ref} className={popoverAnchor} {...rest} />;
};
Anchor.displayName = 'Popover.Anchor';

type TriggerProps = ComponentProps<typeof RadixPopoverTrigger> & {
  ref?: Ref<HTMLButtonElement>;
};

const Trigger = (props: TriggerProps) => {
  const {ref, ...rest} = props;
  return <RadixPopoverTrigger ref={ref} {...rest} />;
};
Trigger.displayName = 'Popover.Trigger';

type PopoverContentProps = (ComponentProps<typeof RadixPopoverContent> & {
  placement?: PopoverPlacement;
  className?: string;
  showArrow?: boolean;
  /* You'll want to use placement="top" and avoidCollisions={false} when using borderless variant */
  variant?: 'default' | 'borderless';
}) & {ref?: Ref<HTMLDivElement>};

const Content = (props: PopoverContentProps) => {
  const {
    placement = PopoverPlacement.BOTTOM,
    className,
    showArrow = false,
    variant = 'default',
    ref,
    ...rest
  } = props;
  const [side, align] = placement.split('-') as [
    PopoverContentProps['side'],
    PopoverContentProps['align'],
  ];
  const klasses = clsx(
    variant === 'borderless' ? borderlessPopoverContent : popoverContent,
    className
  );

  return (
    <RadixPopoverContent
      ref={ref}
      className={klasses}
      side={side}
      align={align === 'start' || align === 'end' ? align : 'center'}
      {...rest}
    >
      {rest.children}
      {showArrow && (
        <RadixPopoverArrow asChild>
          <Arrow variant={variant} />
        </RadixPopoverArrow>
      )}
    </RadixPopoverContent>
  );
};
Content.displayName = 'Popover.Content';

type CloseProps = WithoutClassName<ComponentProps<typeof RadixPopoverClose>> & {
  ref?: Ref<HTMLButtonElement>;
};

const Close = (props: CloseProps) => {
  const {ref, ...rest} = props;
  return <RadixPopoverClose ref={ref} {...rest} />;
};
Close.displayName = 'Popover.Close';

export const Popover = {
  Root,
  Portal,
  Anchor,
  Trigger,
  Content,
  Close,
};
